/**
 * CR228: Empty State Block
 * 
 * Layout-driven empty state messaging. Replaces hardcoded empty state copy.
 * Layout Studio controls the copy and appearance.
 * 
 * This block only renders when the filtered deals count is zero.
 */

import { Search, Package, Calendar } from "lucide-react";
import type { BlockProps } from "./types";
import { useMemo } from "react";

export interface EmptyStateBlockConfig {
  /** Primary message when no deals match filters */
  noMatchMessage?: string;
  /** Message when there are no deals at all */
  noDealsMessage?: string;
  /** Icon style: search, package, calendar */
  iconStyle?: "search" | "package" | "calendar";
  /** Whether to show helpful tips */
  showTips?: boolean;
}

const ICONS = {
  search: Search,
  package: Package,
  calendar: Calendar,
};

export function EmptyStateBlock({ config, context }: BlockProps<EmptyStateBlockConfig>) {
  const noMatchMessage = config?.noMatchMessage ?? "No deals match your current filters.";
  const noDealsMessage = config?.noDealsMessage ?? "Currently there are no deals available.";
  const iconStyle = config?.iconStyle ?? "search";
  const showTips = config?.showTips ?? true;
  
  // Calculate filtered deals (same logic as ResultsBlock)
  const { filteredCount, totalConfirmed, isFiltered } = useMemo(() => {
    const confirmed = context.deals.filter((d) => d.confidence !== "Low");
    const total = confirmed.length;
    
    // Check if any filters are active
    const hasSearch = context.searchQuery.trim().length >= 1;
    const hasCategory = context.selectedCategory !== "All";
    const hasDealType = context.selectedDealTypes !== "All";
    const filtered = hasSearch || hasCategory || hasDealType;
    
    return {
      filteredCount: total, // Simplified - actual filtering happens in ResultsBlock
      totalConfirmed: total,
      isFiltered: filtered,
    };
  }, [context.deals, context.searchQuery, context.selectedCategory, context.selectedDealTypes]);
  
  // Only render if there are no confirmed deals at all
  // Note: This block shows the "no deals available" state
  // The "no matches" state is handled by ResultsBlock showing 0 results
  if (totalConfirmed > 0) {
    return null;
  }
  
  const Icon = ICONS[iconStyle];
  const message = isFiltered ? noMatchMessage : noDealsMessage;
  
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
        <Icon className="w-8 h-8 text-muted-foreground" />
      </div>
      <p className="text-lg font-serif text-foreground">
        {message}
      </p>
      {showTips && isFiltered && (
        <p className="text-sm text-muted-foreground mt-2">
          Try adjusting your filters or check back later.
        </p>
      )}
    </div>
  );
}
